if (!customElements.get('regalux-sticky-cart')) {
  customElements.define(
    'regalux-sticky-cart',
    class RegaluxStickyCart extends HTMLElement {
      connectedCallback() {
        if (this.connected) return;
        this.connected = true;

        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;
        this.mainProduct = document.getElementById(`MainProduct-${this.sectionId}`);
        this.productInfo = document.getElementById(`ProductInfo-${this.sectionId}`) || this.mainProduct;
        this.visibilityTargetId =
          this.dataset.visibilityTargetId || `RegaluxSmartBundle-${this.sectionId}`;
        this.quantityInput = this.querySelector('[data-sticky-quantity]');
        this.minusButton = this.querySelector('[data-sticky-minus]');
        this.plusButton = this.querySelector('[data-sticky-plus]');
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.submitLabel = this.querySelector('[data-sticky-submit-label]');
        this.price = this.querySelector('[data-sticky-price]');
        this.comparePrice = this.querySelector('[data-sticky-compare-price]');
        this.image = this.querySelector('[data-sticky-image]');

        if (!this.mainProduct || !this.quantityInput || !this.submitButton) return;

        this.handleScroll = this.queueVisibilityUpdate.bind(this);
        this.handleProductEvent = this.onProductEvent.bind(this);
        this.handleMinus = () => this.changeQuantity(-1);
        this.handlePlus = () => this.changeQuantity(1);
        this.handleQuantityChange = () => this.commitStickyQuantity();
        this.handleSubmit = this.onSubmit.bind(this);

        this.minusButton?.addEventListener('click', this.handleMinus);
        this.plusButton?.addEventListener('click', this.handlePlus);
        this.quantityInput.addEventListener('change', this.handleQuantityChange);
        this.quantityInput.addEventListener('blur', this.handleQuantityChange);
        this.submitButton.addEventListener('click', this.handleSubmit);
        this.mainProduct.addEventListener('change', this.handleProductEvent, true);
        this.mainProduct.addEventListener('input', this.handleProductEvent, true);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleScroll, { passive: true });

        if (this.productInfo) {
          this.productObserver = new MutationObserver(() => this.queueSync());
          this.productObserver.observe(this.productInfo, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['aria-disabled', 'class', 'disabled', 'max', 'min', 'step', 'value'],
          });
        }

        this.syncFromProduct();
        this.updateVisibility();
      }

      disconnectedCallback() {
        if (!this.connected) return;
        this.connected = false;

        this.minusButton?.removeEventListener('click', this.handleMinus);
        this.plusButton?.removeEventListener('click', this.handlePlus);
        this.quantityInput?.removeEventListener('change', this.handleQuantityChange);
        this.quantityInput?.removeEventListener('blur', this.handleQuantityChange);
        this.submitButton?.removeEventListener('click', this.handleSubmit);
        this.mainProduct?.removeEventListener('change', this.handleProductEvent, true);
        this.mainProduct?.removeEventListener('input', this.handleProductEvent, true);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleScroll);
        this.productObserver?.disconnect();
        cancelAnimationFrame(this.syncFrame);
        cancelAnimationFrame(this.visibilityFrame);
        this.syncTimers?.forEach((timer) => clearTimeout(timer));
      }

      bindProductControls() {
        this.mainForm = document.getElementById(this.formId);
        this.mainButton = this.mainForm?.querySelector('[name="add"][type="submit"]');

        if (!this.mainForm || !this.mainButton) return false;

        this.mainQuantity = Array.from(this.mainProduct.querySelectorAll('input[name="quantity"]')).find(
          (input) => input.form === this.mainForm
        );

        if (!this.mainQuantity) {
          this.mainQuantity = this.mainForm.querySelector('[data-regalux-sticky-quantity-proxy]');
        }

        if (!this.mainQuantity) {
          this.mainQuantity = document.createElement('input');
          this.mainQuantity.type = 'hidden';
          this.mainQuantity.name = 'quantity';
          this.mainQuantity.value = this.quantityInput.value || '1';
          this.mainQuantity.setAttribute('min', this.quantityInput.min || '1');
          this.mainQuantity.setAttribute('step', this.quantityInput.step || '1');
          if (this.quantityInput.max) this.mainQuantity.setAttribute('max', this.quantityInput.max);
          this.mainQuantity.dataset.regaluxStickyQuantityProxy = '';
          this.mainForm.appendChild(this.mainQuantity);
        }

        this.visibilityTarget = this.getVisibilityTarget();
        return true;
      }

      getVisibilityTarget() {
        const bundleTarget =
          document.getElementById(this.visibilityTargetId) ||
          this.productInfo?.querySelector('regalux-smart-bundle, .rsb-widget--block');

        if (bundleTarget) return bundleTarget;

        return this.mainButton.closest('.product-form__buttons') || this.mainButton;
      }

      onProductEvent(event) {
        if (event.target === this.mainQuantity) {
          this.queueSync();
          return;
        }

        this.queueSync();
        this.scheduleSyncBurst();
      }

      scheduleSyncBurst() {
        this.syncTimers?.forEach((timer) => clearTimeout(timer));
        this.syncTimers = [120, 350, 800].map((delay) =>
          setTimeout(() => {
            this.syncFromProduct();
            this.updateVisibility();
          }, delay)
        );
      }

      queueSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.syncFromProduct();
          this.updateVisibility();
        });
      }

      queueVisibilityUpdate() {
        if (this.visibilityFrame) return;
        this.visibilityFrame = requestAnimationFrame(() => {
          this.visibilityFrame = null;
          this.updateVisibility();
        });
      }

      syncFromProduct() {
        if (!this.bindProductControls()) {
          this.setVisible(false);
          return;
        }

        this.syncQuantityFromProduct();
        this.syncPriceFromProduct();
        this.syncButtonFromProduct();
      }

      syncQuantityFromProduct() {
        const minimum = this.readNumber(this.mainQuantity.dataset.min || this.mainQuantity.min, 1);
        const maximumValue = this.mainQuantity.dataset.max || this.mainQuantity.max;
        const maximum = maximumValue === '' || maximumValue == null ? null : this.readNumber(maximumValue, null);
        const step = this.readNumber(this.mainQuantity.step, 1);
        const value = this.normaliseQuantity(this.mainQuantity.value, minimum, maximum, step);

        this.quantityInput.min = String(minimum);
        this.quantityInput.step = String(step);
        if (maximum == null) this.quantityInput.removeAttribute('max');
        else this.quantityInput.max = String(maximum);
        this.quantityInput.value = String(value);
        this.updateQuantityButtons(value, minimum, maximum, step);
      }

      syncPriceFromProduct() {
        const priceContainer = document.getElementById(`price-${this.sectionId}`);
        const priceRoot = priceContainer?.querySelector('.price');
        if (!priceRoot || !this.price) return;

        const onSale = priceRoot.classList.contains('price--on-sale');
        const currentPrice = onSale
          ? priceRoot.querySelector('.price__sale .price-item--sale')
          : priceRoot.querySelector('.price__regular .price-item--regular');
        const comparePrice = onSale ? priceRoot.querySelector('.price__sale .price-item--regular') : null;
        const currentMarkup = this.getPriceMarkup(currentPrice);
        const compareMarkup = this.getPriceMarkup(comparePrice, true);
        const currentLabel = this.getFormattedPrice(currentPrice);
        const compareLabel = this.getFormattedPrice(comparePrice);

        if (currentMarkup) {
          this.price.innerHTML = currentMarkup;
          if (currentLabel) this.price.setAttribute('aria-label', currentLabel);
        }
        if (this.comparePrice) {
          this.comparePrice.innerHTML = compareMarkup;
          this.comparePrice.hidden = !compareMarkup;
          if (compareLabel) this.comparePrice.setAttribute('aria-label', compareLabel);
          else this.comparePrice.removeAttribute('aria-label');
        }
      }

      syncButtonFromProduct() {
        const isLoading = this.mainButton.classList.contains('loading');
        const isDisabled =
          this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true' || isLoading;
        const label = isLoading
          ? this.dataset.loadingLabel
          : this.getMainButtonLabel() || (isDisabled ? this.dataset.soldOutLabel : this.dataset.addLabel);

        this.submitButton.disabled = isDisabled;
        this.submitButton.setAttribute('aria-disabled', String(isDisabled));
        this.submitButton.classList.toggle('is-loading', isLoading);
        if (label) this.submitLabel.textContent = label;
      }

      getMainButtonLabel() {
        const soldOutMessage = this.mainButton.querySelector('.sold-out-message:not(.hidden)');
        if (soldOutMessage) return this.cleanText(soldOutMessage.textContent);

        const directLabel = Array.from(this.mainButton.children).find(
          (child) => child.tagName === 'SPAN' && !child.classList.contains('hidden') && !child.classList.contains('loading__spinner')
        );
        return this.cleanText(directLabel?.textContent);
      }

      changeQuantity(direction) {
        const minimum = this.readNumber(this.quantityInput.min, 1);
        const maximum = this.quantityInput.max === '' ? null : this.readNumber(this.quantityInput.max, null);
        const step = this.readNumber(this.quantityInput.step, 1);
        const current = this.readNumber(this.quantityInput.value, minimum);
        const next = this.normaliseQuantity(current + direction * step, minimum, maximum, step);

        this.quantityInput.value = String(next);
        this.commitStickyQuantity();
      }

      commitStickyQuantity() {
        if (!this.bindProductControls()) return;

        const minimum = this.readNumber(this.quantityInput.min, 1);
        const maximum = this.quantityInput.max === '' ? null : this.readNumber(this.quantityInput.max, null);
        const step = this.readNumber(this.quantityInput.step, 1);
        const value = this.normaliseQuantity(this.quantityInput.value, minimum, maximum, step);

        this.quantityInput.value = String(value);
        this.updateQuantityButtons(value, minimum, maximum, step);

        if (String(this.mainQuantity.value) !== String(value)) {
          this.mainQuantity.value = String(value);
          this.mainQuantity.dispatchEvent(new Event('input', { bubbles: true }));
          this.mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      updateQuantityButtons(value, minimum, maximum, step) {
        if (this.minusButton) this.minusButton.disabled = value - step < minimum;
        if (this.plusButton) this.plusButton.disabled = maximum != null && value + step > maximum;
      }

      normaliseQuantity(rawValue, minimum, maximum, step) {
        let value = this.readNumber(rawValue, minimum);
        value = Math.max(minimum, value);
        if (maximum != null) value = Math.min(maximum, value);

        const stepCount = Math.round((value - minimum) / step);
        value = minimum + stepCount * step;

        if (maximum != null && value > maximum) {
          const maximumStepCount = Math.max(0, Math.floor((maximum - minimum) / step));
          value = minimum + maximumStepCount * step;
        }

        return Math.round(value * 1000000) / 1000000;
      }

      readNumber(value, fallback) {
        if (value === '' || value == null) return fallback;
        const number = Number(value);
        return Number.isFinite(number) ? number : fallback;
      }

      cleanText(value) {
        return value?.replace(/\s+/g, ' ').trim() || '';
      }

      getFormattedPrice(source) {
        if (!source) return '';

        const priceCopy = source.cloneNode(true);
        priceCopy.querySelectorAll('.badge').forEach((badge) => badge.remove());
        priceCopy.querySelectorAll('.decimal-price').forEach((decimal) => {
          const decimalValue = this.cleanText(decimal.textContent);
          decimal.replaceWith(document.createTextNode(decimalValue ? `,${decimalValue}` : ''));
        });

        return this.cleanText(priceCopy.textContent);
      }

      getPriceMarkup(source, unwrapStrikethrough = false) {
        if (!source) return '';

        const priceCopy = source.cloneNode(true);
        priceCopy.querySelectorAll('.badge').forEach((badge) => badge.remove());

        if (unwrapStrikethrough) {
          priceCopy.querySelectorAll('s').forEach((strikethrough) => {
            strikethrough.replaceWith(...Array.from(strikethrough.childNodes));
          });
        }

        return priceCopy.innerHTML.trim();
      }

      onSubmit() {
        if (!this.bindProductControls()) return;
        if (
          this.mainButton.disabled ||
          this.mainButton.getAttribute('aria-disabled') === 'true' ||
          this.mainButton.classList.contains('loading')
        ) {
          this.syncButtonFromProduct();
          return;
        }

        this.commitStickyQuantity();
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitLabel.textContent = this.dataset.loadingLabel || this.dataset.addLabel;

        this.mainButton.click();
        this.scheduleSyncBurst();
      }

      updateVisibility() {
        if (!this.bindProductControls() || !this.visibilityTarget?.getClientRects().length) {
          this.setVisible(false);
          return;
        }

        const targetRect = this.visibilityTarget.getBoundingClientRect();
        this.setVisible(targetRect.bottom <= 0);
      }

      setVisible(shouldShow) {
        if (shouldShow) {
          this.hidden = false;

          if (
            this.image &&
            !this.image.getAttribute('src')
          ) {
            const imageSource = this.image.dataset.src;
            if (imageSource) this.image.src = imageSource;
          }
        }

        if (this.isVisible === shouldShow) return;
        this.isVisible = shouldShow;
        this.classList.toggle('is-visible', shouldShow);
        this.setAttribute('aria-hidden', String(!shouldShow));
      }
    }
  );
}
